import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { updateProfile, uploadVideoIntro, createCheckout } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  User, Video, Shield, CheckCircle, Upload, Star, Camera
} from "lucide-react";

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    bio: user?.bio || "",
    military_status: user?.military_status || "",
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(form);
      await refreshUser();
      setEditing(false);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      await uploadVideoIntro(file);
      await refreshUser();
    } catch (err) { console.error(err); }
    setUploadingVideo(false);
  };

  const handleBackgroundCheck = async () => {
    try {
      const res = await createCheckout("background_check", window.location.origin);
      window.location.href = res.data.url;
    } catch (err) { console.error(err); }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d0d0d] font-['Fraunces'] mb-8" data-testid="profile-heading">
          My Profile
        </h1>

        {/* Profile Header */}
        <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm mb-6">
          <div className="flex items-center gap-4 mb-6">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-20 h-20 rounded-full object-cover border-4 border-[#faf8f3]" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#1a6b3c] flex items-center justify-center text-white text-2xl font-bold">
                {user?.name?.charAt(0)}
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-[#0d0d0d] font-['Fraunces']">{user?.name}</h2>
              <p className="text-sm text-[#5a5a5a]">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-[#1a6b3c]/10 text-[#1a6b3c] border-0 rounded-full text-xs capitalize">
                  {user?.role}
                </Badge>
                {user?.trust_score > 0 && (
                  <Badge className="bg-[#c8f563]/30 text-[#0d0d0d] border-0 rounded-full text-xs flex items-center gap-1">
                    <Star className="w-3 h-3" /> {user?.trust_score}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {!editing ? (
            <div className="space-y-3">
              {user?.phone && <p className="text-sm text-[#5a5a5a]">Phone: {user.phone}</p>}
              {user?.bio && <p className="text-sm text-[#5a5a5a]">{user.bio}</p>}
              {user?.military_status && <p className="text-sm text-[#5a5a5a]">Military: {user.military_status}</p>}
              <Button data-testid="edit-profile-btn" onClick={() => setEditing(true)} variant="outline" className="rounded-xl border-[#d5d3ce]">
                Edit Profile
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Name</label>
                <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-xl border-[#d5d3ce]" data-testid="profile-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Phone</label>
                <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="rounded-xl border-[#d5d3ce]" data-testid="profile-phone-input" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Bio</label>
                <Textarea value={form.bio} onChange={(e) => setForm(f => ({ ...f, bio: e.target.value }))} className="rounded-xl border-[#d5d3ce]" data-testid="profile-bio-input" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Military Status</label>
                <Select value={form.military_status} onValueChange={(v) => setForm(f => ({ ...f, military_status: v }))}>
                  <SelectTrigger className="rounded-xl border-[#d5d3ce]"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active_duty">Active Duty</SelectItem>
                    <SelectItem value="veteran">Veteran</SelectItem>
                    <SelectItem value="military_spouse">Military Spouse</SelectItem>
                    <SelectItem value="none">Not Military</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button data-testid="save-profile-btn" onClick={handleSave} disabled={saving} className="rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white">
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-xl">Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {/* Verification Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Video Intro */}
          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm" data-testid="video-intro-card">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${user?.video_intro_path ? "bg-[#1a6b3c]/10" : "bg-[#e5e3de]"}`}>
                <Video className={`w-5 h-5 ${user?.video_intro_path ? "text-[#1a6b3c]" : "text-[#5a5a5a]"}`} />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Video Intro</p>
                <Badge className={`text-xs mt-0.5 ${user?.video_intro_path ? "bg-[#1a6b3c]/10 text-[#1a6b3c]" : "bg-[#e5e3de] text-[#5a5a5a]"} border-0`}>
                  {user?.video_intro_path ? "Uploaded" : "Not uploaded"}
                </Badge>
              </div>
            </div>
            <label className="w-full cursor-pointer">
              <div className="flex items-center justify-center gap-2 border-2 border-dashed border-[#d5d3ce] rounded-xl py-3 px-4 hover:border-[#1a6b3c] transition-colors">
                {uploadingVideo ? (
                  <span className="text-sm text-[#5a5a5a]">Uploading...</span>
                ) : (
                  <>
                    <Camera className="w-4 h-4 text-[#5a5a5a]" />
                    <span className="text-sm text-[#5a5a5a]">{user?.video_intro_path ? "Replace Video" : "Upload 60s Video"}</span>
                  </>
                )}
              </div>
              <input type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} disabled={uploadingVideo} data-testid="video-upload-input" />
            </label>
          </div>

          {/* Background Check */}
          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm" data-testid="bg-check-profile-card">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${user?.background_check_status === "completed" ? "bg-[#1a6b3c]/10" : "bg-[#e5e3de]"}`}>
                <Shield className={`w-5 h-5 ${user?.background_check_status === "completed" ? "text-[#1a6b3c]" : "text-[#5a5a5a]"}`} />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Background Check</p>
                <Badge className={`text-xs mt-0.5 ${user?.background_check_status === "completed" ? "bg-[#1a6b3c]/10 text-[#1a6b3c]" : "bg-[#e5e3de] text-[#5a5a5a]"} border-0`}>
                  {user?.background_check_status === "completed" ? "Completed" : "Not started"}
                </Badge>
              </div>
            </div>
            {user?.background_check_status !== "completed" && (
              <Button data-testid="bg-check-profile-btn" onClick={handleBackgroundCheck} className="w-full rounded-xl bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] text-sm font-semibold">
                Start Check — $36.05
                <span className="text-[10px] font-normal ml-1 opacity-70">($35 + 3% fee)</span>
              </Button>
            )}
            {user?.background_check_status === "completed" && (
              <div className="flex items-center gap-2 text-sm text-[#1a6b3c]">
                <CheckCircle className="w-4 h-4" /> Criminal, Eviction, Credit & Identity verified
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
